// src/hooks/useBulkProductImport.js
import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { bulkImportProducts } from '../services/api/products';
import { generateBulkProductTemplate } from '../utils/excelTemplateGenerator';
import { resolveColumn } from '../utils/bulkColumnMapping';

const CHUNK_SIZE = 50;

const useBulkProductImport = () => {
  const [selectedShops,   setSelectedShops]   = useState([]); // array of shop objects
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // single warehouse object or null
  const [uploadedFile,      setUploadedFile]      = useState(null);
  const [parsedData,        setParsedData]        = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [submissionResults, setSubmissionResults] = useState(null);
  const [isProcessing,      setIsProcessing]      = useState(false);
  const [processingStep,    setProcessingStep]    = useState('select');
  const [progress,          setProgress]          = useState({ current: 0, total: 0 });
  const [columnMappings,    setColumnMappings]    = useState({});

  // ── Parse Excel ───────────────────────────────────────────────────────────
  const parseExcelFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data     = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          let sheetName = 'Products';
          if (!workbook.SheetNames.includes(sheetName)) {
            sheetName = 'Sheet1';
            if (!workbook.SheetNames.includes(sheetName)) {
              sheetName = workbook.SheetNames[0];
            }
          }

          const worksheet = workbook.Sheets[sheetName];
          const jsonData  = XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: null });

          const transformedData = jsonData.map((row, index) => {
            const r = (canonicalKey) => resolveColumn(row, canonicalKey, columnMappings);

            return {
              rowNumber:    index + 2,
              originalRow:  { ...row },
              sku:          (r('SKU') ?? '').toString().trim(),
              name:         (r('Name') ?? '').toString().trim(),
              printableName: r('Printable_Name') || null,
              description:  r('Description')  || null,
              costPrice:    r('Cost_Price')    ?? '',
              barcode:      r('Barcode')       || null,
              weight:       r('Weight')        ?? '',
              category:     r('Category')      || null,
              brand:        r('Brand')         || null,
              price:        r('Price')         ?? '',
              openingStock: r('Opening_Stock') ?? '',
              visibility:   (r('Visibility') ?? '').toString().trim().toLowerCase() || null,
            };
          });

          resolve(transformedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, [columnMappings]);

  // ── Handle file upload ────────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    setIsProcessing(true);
    setProcessingStep('upload');
    try {
      const parsed = await parseExcelFile(file);
      setUploadedFile(file);
      setParsedData(parsed);
      setValidationResults(null);
      setSubmissionResults(null);
      setProcessingStep('validate');
    } catch (error) {
      console.error('File parsing error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [parseExcelFile]);

  const mapToRowPayload = (data) => ({
    rowNumber:    data.rowNumber,
    sku:          data.sku,
    name:         data.name,
    printableName: data.printableName,
    description:  data.description,
    costPrice:    data.costPrice,
    barcode:      data.barcode,
    weight:       data.weight,
    category:     data.category,
    brand:        data.brand,
    price:        data.price,
    openingStock: data.openingStock,
    visibility:   data.visibility,
  });

  const chunkArray = (array) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += CHUNK_SIZE) chunks.push(array.slice(i, i + CHUNK_SIZE));
    return chunks;
  };

  const shopIdList = useCallback(
    () => selectedShops.map(s => s.id),
    [selectedShops],
  );

  // ── Validation ────────────────────────────────────────────────────────────
  const handleValidation = useCallback(async () => {
    if (selectedShops.length === 0 || parsedData.length === 0) return;
    setIsProcessing(true);
    setProcessingStep('validate');
    setProgress({ current: 0, total: parsedData.length });

    try {
      const rows       = parsedData.map(mapToRowPayload);
      const chunks     = chunkArray(rows);
      const allResults = [];

      for (const chunk of chunks) {
        const chunkResult = await bulkImportProducts(
          shopIdList(), selectedWarehouse?.id || null, chunk, true,
        );
        allResults.push(...chunkResult.results);
        setProgress({ current: allResults.length, total: rows.length });
      }

      const validCount   = allResults.filter(r => r.status === 'valid').length;
      const failureCount = allResults.filter(r => r.status === 'failed').length;

      setValidationResults({
        summary: { totalSubmitted: allResults.length, validCount, failureCount },
        results: allResults,
      });
      setProgress({ current: allResults.length, total: allResults.length });
    } catch (error) {
      console.error('Validation error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShops, selectedWarehouse, parsedData, shopIdList]);

  // ── Retry validation ──────────────────────────────────────────────────────
  const handleRetryValidation = useCallback(async () => {
    if (selectedShops.length === 0 || !validationResults) return;
    setIsProcessing(true);
    setProcessingStep('validate');

    try {
      const invalidRowNumbers = validationResults.results
        .filter(r => r.status === 'failed')
        .map(r => r.rowNumber);

      const rowsToRetry = parsedData
        .filter(d => invalidRowNumbers.includes(d.rowNumber))
        .map(mapToRowPayload);

      setProgress({ current: 0, total: rowsToRetry.length });

      const chunks          = chunkArray(rowsToRetry);
      const allRetryResults = [];

      for (const chunk of chunks) {
        const chunkResult = await bulkImportProducts(
          shopIdList(), selectedWarehouse?.id || null, chunk, true,
        );
        allRetryResults.push(...chunkResult.results);
        setProgress({ current: allRetryResults.length, total: rowsToRetry.length });
      }

      const mergedResults = validationResults.results.map(oldResult => {
        if (oldResult.status === 'valid') return oldResult;
        return allRetryResults.find(r => r.rowNumber === oldResult.rowNumber) || oldResult;
      });

      const validCount   = mergedResults.filter(r => r.status === 'valid').length;
      const failureCount = mergedResults.filter(r => r.status === 'failed').length;

      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, validCount, failureCount },
        results: mergedResults,
      });
    } catch (error) {
      console.error('Retry validation error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShops, selectedWarehouse, parsedData, validationResults, shopIdList]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (selectedShops.length === 0 || !validationResults) return;
    setIsProcessing(true);
    setProcessingStep('submit');

    try {
      const validRowNumbers = validationResults.results
        .filter(r => r.status === 'valid')
        .map(r => r.rowNumber);

      const rowsToSubmit = parsedData
        .filter(d => validRowNumbers.includes(d.rowNumber))
        .map(mapToRowPayload);

      setProgress({ current: 0, total: rowsToSubmit.length });

      const chunks     = chunkArray(rowsToSubmit);
      const allResults = [];

      for (const chunk of chunks) {
        const chunkResult = await bulkImportProducts(
          shopIdList(), selectedWarehouse?.id || null, chunk, false,
        );
        allResults.push(...chunkResult.results);
        setProgress({ current: allResults.length, total: rowsToSubmit.length });
      }

      const successCount = allResults.filter(r => r.status === 'success').length;
      const failureCount = allResults.filter(r => r.status === 'failed').length;

      setSubmissionResults({
        success: failureCount === 0,
        summary: { totalSubmitted: rowsToSubmit.length, successCount, failureCount },
        results: allResults,
      });
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShops, selectedWarehouse, parsedData, validationResults, shopIdList]);

  // ── Remove single row ─────────────────────────────────────────────────────
  const removeInvalidRow = useCallback((rowNumber) => {
    setParsedData(prev => prev.filter(d => d.rowNumber !== rowNumber));

    if (validationResults) {
      const newResults   = validationResults.results.filter(r => r.rowNumber !== rowNumber);
      const validCount   = newResults.filter(r => r.status === 'valid').length;
      const failureCount = newResults.filter(r => r.status === 'failed').length;
      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, totalSubmitted: newResults.length, validCount, failureCount },
        results: newResults,
      });
    }
  }, [validationResults]);

  // ── Update row ────────────────────────────────────────────────────────────
  const updateInvalidRow = useCallback((rowNumber, field, value) => {
    setParsedData(prev => prev.map(data => (
      data.rowNumber === rowNumber ? { ...data, [field]: value } : data
    )));
  }, []);

  // ── Clear all ─────────────────────────────────────────────────────────────
  const clearAll = useCallback(() => {
    setUploadedFile(null);
    setParsedData([]);
    setValidationResults(null);
    setSubmissionResults(null);
    setProgress({ current: 0, total: 0 });
    setProcessingStep('select');
  }, []);

  // ── Download template ─────────────────────────────────────────────────────
  const downloadTemplate = useCallback(async () => {
    const workbook = await generateBulkProductTemplate(selectedWarehouse?.name);
    const buffer   = await workbook.xlsx.writeBuffer();
    const blob     = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `bulk_product_template_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [selectedWarehouse]);

  return {
    selectedShops,
    selectedWarehouse,
    uploadedFile,
    parsedData,
    validationResults,
    submissionResults,
    isProcessing,
    processingStep,
    progress,
    columnMappings,

    setSelectedShops,
    setSelectedWarehouse,
    setColumnMappings,
    handleFileUpload,
    handleValidation,
    handleSubmit,
    handleRetryValidation,
    removeInvalidRow,
    updateInvalidRow,
    clearAll,
    downloadTemplate,
  };
};

export default useBulkProductImport;
